'use client';

import { useMemo, type FC } from 'react';
import type { IImage } from '@/services/models/Media/Image';
import { PDPVideo } from '@/react/components/product/PDPVideo';
import type { IPlayerSettings, IVideo } from '@/services/models/Media/Video';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { ISliderProps, Slide, Slider } from '../../../Slider';
import { Image } from '../../../core-ui/Image';

import S from './styles.base.module.scss';

export interface IQuickviewCarouselProps {
  /** Images to display in the carousel. */
  images: Array<IImage>;

  /** Videos to display in the carousel. */
  videos: Array<IVideo>;
}

/** The main carousel for the PDP. */
export const QuickviewCarousel: FC<IQuickviewCarouselProps> = ({
  images,
  videos
}) => {
  const sliderConfig = {
    height: '100%',
    loop: true,
    sliderClassName: S.sliderContainer,
    navigationWrapperClassName: S.navigationWrapper,
    navigation: {
      enabled: true,
      inset: true,
      arrowVariant: 'angleLight',
      buttonClassName: S.navigation
    },
    pagination: {
      enabled: true,
      inset: false,
      activeClassName: S.activeCircle,
      wrapperClassName: S.pagination,
      buttonClassName: S.circle
    }
  } as ISliderProps;

  const media = useMemo(
    () => [
      ...images.map((image) => ({ ...image, isVideo: false })),
      ...videos.map((video) => ({ ...video, isVideo: true }))
    ],
    [videos, images]
  );

  const videoSettingsConfig = ConfigurationService.getConfig(
    'product'
  ).getSetting('details.videoSettingsDesktop');

  const videoSettings: IPlayerSettings = {
    autoplay: videoSettingsConfig.autoplay.value,
    muted: videoSettingsConfig.muted.value,
    loop: videoSettingsConfig.loop.value
  };

  return (
    <div className={S.container}>
      <Slider {...sliderConfig}>
        {media.map(({ isVideo, ...media }) => (
          <Slide key={media.uuid}>
            {isVideo ? (
              <PDPVideo video={{ ...media, settings: videoSettings }} />
            ) : (
              <Image image={media} className={S.imageContainer} />
            )}
          </Slide>
        ))}
      </Slider>
    </div>
  );
};
